import Component from 'vue-class-component';
import Moment from 'moment';
import { Mixins, Prop } from 'vue-property-decorator';
import { ScheduleBlock } from '@/tools/models/class-builder.models';
import { AB_DAYS, WEEK_DAYS } from '@/values/general.values';
import { ClassBuilderCalendarMixin } from '../class-builder-calendar.mixin';
import { CalendarEventDetail } from '@webcat/commons-web-components/dist/calendar/models/calendar-event-detail';
import { mouseEnter } from '@/constants/constants-components';
import { EdContextMenuModel } from '@/plugins/ed-context-menu/ed-context-menu.plugin';
import { EdGenericCalendarWeekdays } from '@/core/components/ed-generic-calendar/interfaces/ed-generic-calendar-weekdays.interface';

@Component({
  name: 'ed-class-schedule-calendar',
  components: {
    EdGenericCalendar: () => import(/* EdGenericCalendar */ '@/core/components/ed-generic-calendar/ed-generic-calendar.component.vue'),
  }
})
export default class EdClassScheduleCalendarComponent extends Mixins(ClassBuilderCalendarMixin) {
  @Prop({ default: () => [] }) readonly blocksCreated: ScheduleBlock[];
  @Prop({ default: () => [0, 1, 2, 3, 4, 5, 6] }) readonly weekdays: number[] | EdGenericCalendarWeekdays[];

  isSelecting = false;
  currentDate = new Date();

  get sortedWeekdaysWithLabels(): number[] | EdGenericCalendarWeekdays[] {
    const hasLabels = this.weekdays.some((item: number | EdGenericCalendarWeekdays) => {
      return typeof item === 'object';
    });

    if (hasLabels) {
      const weekdays = this.weekdays as EdGenericCalendarWeekdays[];
      return weekdays.sort(
        (a: EdGenericCalendarWeekdays, b: EdGenericCalendarWeekdays) => a.day - b.day)
        .map(item => ({
          ...item,
          label: this.$t(`tools.classBuilder.${item.key}`) as string
        }));
    }

    return this.weekdays.sort();
  }

  get sortedWeekdays(): number[] {
    return this.sortedWeekdaysWithLabels.map((item: number | EdGenericCalendarWeekdays) => {
      if (typeof item === 'number') {
        return item;
      }

      return item.day;
    });
  }

  get selectedDate(): string {
    const date = new Date(this.currentDate);
    if (this.sortedWeekdays.length > 0) {
      const currentDay = date.getDay();
      const targetDay = this.sortedWeekdays[0];
      const diff = targetDay - currentDay;
      date.setDate(date.getDate() + diff);
    }
    const formattedDate = date.toISOString().substring(0, 10);
    return formattedDate
  }
  
  get events(): CalendarEventDetail[] {
    return this._mapToCalendarEvent(this.calendarEvents);
  }

  get calendarEvents() {
    const events = [];
    
    this.blocksCreated.forEach((block) => {
      const { title } = block;

      block.scheduleEntries.forEach((entry) => {
        const listDays = this.ScheduleProvider.isWeeklySchedule ? WEEK_DAYS : AB_DAYS;
        const dayNumber = listDays.indexOf(entry.day.toLocaleLowerCase());
        const date = Moment().weekday(dayNumber).format('YYYY-MM-DD');
  
        events.push({
          name: title,
          id: entry.id,
          start: Moment(`${date} ${entry.start}`).toDate(),
          end: Moment(`${date} ${entry.end}`).toDate(),
        });
      });
    });

    return events;
  }

  private _mapToCalendarEvent(scheduleEntries: any[]): CalendarEventDetail[] {
    const result = scheduleEntries.map(entry => ({
      uid: `${entry.id}`,
      startDate: entry.start,
      endDate: entry.end,
      summary: entry.name,
      allDay: false,
      custom: {
        events: [
          {
            target: `.block-${entry.id}`,
            type: mouseEnter,
            handler: (params) => this._mouseBlockHandler(params, entry.name)
          }
        ]
      }
    }));
    return result;
  }

  private _mouseBlockHandler(params: any, name: string) {
    const type = params.type;
    if (type === mouseEnter) {
      const mouseEvent: MouseEvent = params.mouseEvent;
      if (mouseEvent) {
        this.$EdContextMenu.show(new EdContextMenuModel({
          position: { x: mouseEvent.x, y: mouseEvent.y },
          options: [{ text: name }],
          darkMode: true
        }));
      }
    } else {
      this.$EdContextMenu.close();
    }
  }
}